import React, { useState, useEffect } from "react";
import { DeleteRounded, PictureAsPdf } from "@mui/icons-material";
import { DeleteForever } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import AddBoxIcon from '@mui/icons-material/AddBox';
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Autocomplete,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";

// Importaciones para PDF
import { pdf, PDFDownloadLink } from "@react-pdf/renderer";

import ModalSearchProduct from "../../components/empresa_uno_comp/ModalSearchProduct";
import ModalClienteEnviarPdf from "../ModalClienteEnviarPdf";
import ProductosPdf from "../ProductosPdf";
import { useParams } from 'react-router-dom';
import { ApiService, getCodeColor, useProveedorCompany } from '../../components';

const API_url = process.env.REACT_APP_API_URL;

const SendProductPdf = ({
  isOpen,
  onClose,
  obj_product,
  selectedSucursal,
  branches,
  convertPrice = (() => { }),
}) => {

  const activeBranch = branches?.find(b => b.key_branch === selectedSucursal) || {};
  const { fullName, phone, location_url } = activeBranch;
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [listAll, setListAll] = useState(false);
  const [openClientModal, setOpenClientModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState(obj_product);

  // -- Manejo de estado del PDF subido --
  const [pdfUploadedUrl, setPdfUploadedUrl] = useState(null);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);

  const [isWholesale, setIsWholesale] = useState(false);


  // === A) Sacar categorías y marcas únicas ===
  const uniqueCategories = [
    ...new Set(allProducts.map((p) => p.category).filter(Boolean)),
  ];
  const uniqueBrands = [
    ...new Set(allProducts.map((p) => p.brand).filter(Boolean)),
  ];

  // Estados para select de Categoría y Marca
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // -----------------------------------------------------------------
  // Cada vez que cambiamos la lista de productos, reiniciamos el pdfUrl
  // para obligar a que se genere/suba un nuevo PDF.
  // -----------------------------------------------------------------
  const resetPdfUrl = () => {
    setPdfUploadedUrl(null);
  };

  const { nit } = useParams();
  const { currentCompany } = useProveedorCompany();

  const logo_catalogo = currentCompany?.logo_catalogo ?? null;
  const nameComany = currentCompany?.nameComany ?? "Cargando Nombre...";
  const [pdfBlob, setPdfBlob] = useState(null);
  useEffect(() => {
    const generatePdfBlob = async () => {
      if (products.length === 0) {
        setPdfBlob(null);
        return;
      }
      try {
        const blob = await pdf(
          <ProductosPdf
            convertPrice={convertPrice}
            products={products}
            date={currentDate}
            wholesale={isWholesale}
            themeColor={getCodeColor('var(--theme-company)')}
            logo_catalogo={logo_catalogo}
            nameComany={nameComany}
            nit={nit}
            branchName={fullName}
            phone={phone}
            locationUrl={location_url}

          />
        ).toBlob();
        setPdfBlob(blob);
      } catch (error) {
        console.error("Error al regenerar el PDF:", error);
        setPdfBlob(null);
      }
    };

    generatePdfBlob();
  }, [products, isWholesale]);

  // Manejar el checkbox "Listar todos"
  const handleListAll = (checked) => {
    if (checked) {
      setProducts(allProducts);
      setSelectedCategory(null);
      setSelectedBrand(null);
    } else {
      setProducts([]);
    }
    setListAll(checked);
    resetPdfUrl();
  };

  // Agregar producto
  const handleAddProduct = (product) => {
    if (!products.some((p) => p.code === product.code)) {
      setProducts((prev) => [...prev, product]);
      setListAll(false); // Desactivar "Listar todos"
      resetPdfUrl();
    } else {
      toast.warning("El producto ya está en la lista");
    }
  };

  // Eliminar producto
  const handleRemoveProduct = (code) => {
    const updatedProducts = products.filter((product) => product.code !== code);
    setProducts(updatedProducts);
    resetPdfUrl();

    if (listAll && updatedProducts.length < allProducts.length) {
      setListAll(false);
    }
  };

  // Limpiar la tabla
  const handleClearTable = () => {
    setProducts([]);
    setListAll(false);
    setSelectedCategory(null);
    setSelectedBrand(null);
    resetPdfUrl();
  };

  // Cerrar modal principal
  const handleCloseModal = () => {
    onClose();
  };

  // B) Selección de categoría
  const handleSelectCategory = (event, newValue) => {
    setSelectedBrand(null);
    setListAll(false);
    setProducts([]);
    setSelectedCategory(newValue);
    resetPdfUrl();

    if (newValue) {
      const filtered = allProducts.filter((p) => p.category === newValue);
      setProducts(filtered);
    }
  };

  // C) Selección de marca
  const handleSelectBrand = (event, newValue) => {
    setSelectedCategory(null);
    setListAll(false);
    setProducts([]);
    setSelectedBrand(newValue);
    resetPdfUrl();

    if (newValue) {
      const filtered = allProducts.filter((p) => p.brand === newValue);
      setProducts(filtered);
    }
  };

  if (!isOpen) return null;
  const currentDate = new Date().toLocaleDateString("es-BO");


  // ============================================================== 
  // 1) Función para generar y subir el PDF al servidor
  // ============================================================== 
  const handleGenerateAndUploadPdf = async () => {
    if (products.length === 0) {
      toast.error("Debes agregar al menos un producto para generar el PDF.");
      return null;
    }
    try {
      setIsUploadingPdf(true);

      // Generamos el blob del PDF usando react-pdf
      const blob = await pdf(<ProductosPdf products={products} date={currentDate} convertPrice={convertPrice}/>).toBlob();

      // Creamos el FormData
      const formData = new FormData();
      formData.append("file", blob, "productos.pdf");

      // Llamamos a la API de subir documento
      const response = await ApiService.fetchData("api/upload/document", {
        method: "POST",
        body: formData,
      }, false);

      if (response && response.code === 200) {
        setPdfUploadedUrl(response.url); // Guardamos la url
        toast.success("PDF Listo para enviar a clientes");
        return response.url;
      } else {
        toast.error("No se pudo subir el PDF");
        return null;
      }
    } catch (error) {
      console.error("Error al generar/subir PDF:", error);
      toast.error("Error al subir PDF");
      return null;
    } finally {
      setIsUploadingPdf(false);
    }
  };

  // 2) Función que se dispara al hacer click en "Descargar PDF"
  const handleDownloadPdf = async (e) => {
    // Evitamos que PDFDownloadLink inicie su descarga por defecto
    // e.preventDefault();

    if (products.length === 0) {
      toast.error("Debes agregar al menos un producto para generar el PDF.");
      return;
    }

    // Subimos el PDF antes de descargar
    const uploadedUrl = await handleGenerateAndUploadPdf();
    if (!uploadedUrl) {
      // Si falla la subida, no continuamos
      return;
    }

    toast.info("PDF listo en el servidor, puedes continuar con la descarga.");
  };

  // 3) Cuando el usuario clickea en "Enviar a:"
  const handleOpenClientModal = async () => {
    if (products.length === 0) {
      toast.error("Debes agregar al menos un producto para enviar el PDF.");
      return;
    }
    // Si NO tenemos pdfUploadedUrl, lo generamos y subimos ahora.
    if (!pdfUploadedUrl) {
      const uploadedUrl = await handleGenerateAndUploadPdf();
      if (!uploadedUrl) {
        // Si falla la subida, no abrimos el modal
        return;
      }
    }

    // Abrimos modal de clientes
    setOpenClientModal(true);
  };

  const handleManualDownloadPdf = () => {
    if (!pdfBlob) {
      toast.error("No se ha generado el PDF.");
      return;
    }

    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "productos.pdf";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="modal-overlay-send-products">
      <div className="modal-content-send-products">
        <button className="close-modal-btn" onClick={onClose}>
          ✖
        </button>

        <div className="head-pdf-modal">
          <div className="head-pdf-generator">
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#4ea30b",
                marginLeft: "1px",
                borderRadius: "0px",
              }}
              onClick={() => setIsSearchModalOpen(true)}
            >
              <span style={{ letterSpacing: "0.5px" }} className="text-desktop">Agregar Producto</span>
              <span style={{ letterSpacing: "0.5px" }} className="text-mobile"><AddBoxIcon color="#fff" style={{ fontSize: '35px' }} /></span>
            </Button>

            <div style={{ margin: "10px 0" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={listAll}
                    onChange={(e) => handleListAll(e.target.checked)}
                    color="primary"
                  />
                }
                label="Listar todos"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isWholesale}
                    onChange={(e) => setIsWholesale(e.target.checked)}
                    color="primary"
                  />
                }
                label="Precio Mayorista"
              />
            </div>
          </div>

          <div className="head-pdf-generator">
            <Autocomplete
              value={selectedCategory}
              onChange={handleSelectCategory}
              options={uniqueCategories}
              size="medium"
              sx={{ width: 200, marginTop: "6px" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categorías"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              )}
              filterSelectedOptions
              clearOnEscape
            />

            <Autocomplete
              value={selectedBrand}
              onChange={handleSelectBrand}
              options={uniqueBrands}
              size="medium"
              sx={{ width: 200, marginTop: "6px" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Marcas"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              )}
              filterSelectedOptions
              clearOnEscape
            />
          </div>
        </div>

        {/* Modal de búsqueda de productos */}
        <ModalSearchProduct
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          onAddProduct={handleAddProduct}
          selectedProducts={products}
          allProducts={allProducts}
          selectedCategory={selectedCategory}
          selectedBrand={selectedBrand}
        />

        {/* Tabla de productos */}
        <div className="table-container-send">
          <table className="products-table">
            <thead style={{ backgroundColor: 'var(--theme-company)' }}>
              <tr style={{ backgroundColor: 'var(--theme-company)' }}>
                <th style={{ backgroundColor: 'var(--theme-company)' }}>FOTO</th>
                <th style={{ backgroundColor: 'var(--theme-company)' }}>CÓDIGO</th>
                <th style={{ backgroundColor: 'var(--theme-company)' }}>NOMBRE</th>
                <th style={{ backgroundColor: 'var(--theme-company)' }}>STOCK</th>
                <th style={{ backgroundColor: 'var(--theme-company)' }}>
                  ELIMINAR{" "}
                  <Tooltip title="Eliminar Todos" arrow>
                    <IconButton
                      onClick={handleClearTable}
                      size="small"
                      sx={{
                        color: "#fff",
                        marginLeft: "5px",
                        padding: "3px",
                        backgroundColor: "red",
                        borderRadius: "50px",
                        border: '0.5px solid #000',
                        "&:hover": {
                          backgroundColor: "#ce1919",
                        },
                      }}
                    >
                      <DeleteForever sx={{ fontSize: "20px" }} />
                    </IconButton>
                  </Tooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "20px",
                        marginTop: "0.5rem",
                      }}
                    >
                      No hay productos registrados
                    </p>
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.code}>
                    <td>
                      <img
                        className="img-send-pdf"
                        src={`${API_url}${product.images[0]}`}
                        alt={product.name}
                      />
                    </td>
                    <td>{product.code}</td>
                    <td>{product.name}</td>
                    <td>{product.stock}</td>
                    <td>
                      <IconButton
                        onClick={() => handleRemoveProduct(product.code)}
                        sx={{ color: "red" }}
                      >
                        <DeleteRounded />
                      </IconButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Sección de botones al final */}
        <div className="modal-footer-send" style={{ backgroundColor: 'var(--theme-company)' }}>
          {/* Botón para descargar PDF */}
          <div>
            <Button
              color="secondary"
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleManualDownloadPdf}
              disabled={isUploadingPdf}
              sx={{ textTransform: "none", fontWeight: "bold" }}
            >
              Descargar PDF
            </Button>

          </div>

          {/* Botón para abrir modal de envío a clientes */}
          <div>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#4ea30b",
                textTransform: "none",
                fontWeight: "bold",
              }}
              onClick={handleOpenClientModal}
              disabled={isUploadingPdf}
            >
              {isUploadingPdf ? "Preparando PDF..." : "Enviar a:"}
            </Button>

          </div>

          {/* Modal donde seleccionamos clientes y hacemos el envío del PDF */}
          <ModalClienteEnviarPdf
            selectedSucursal={selectedSucursal}
            open={openClientModal}
            onClose={() => setOpenClientModal(false)}
            pdfUrl={pdfUploadedUrl}
          />
        </div>
      </div>
    </div>
  );
};

export default SendProductPdf;
